import { ValidationErrorType } from "@multiweb/types/multiweb";
import { ReduxCompatibleValue, TypedObject } from "@multiweb/types/react";
import { ControlReduxRawValue } from "../types";

/**
 * Výčet známých validačních funkcí pro "instant"-validaci. Jde o součást {@link CustomizableInstantValidationFnKey}.
 */
export enum InstantValidationFnKey {
    maxLength = "maxLength",
    numbersOnly = "numbersOnly",
    length = "length",
    numberCharactersOnly = "numberCharactersOnly",
}

/**
 * Klíč validační funkce pro "instant"-validaci. Kromě {@link InstantValidationFnKey} je možné jako klíč uvést "libovolný" řetězec z důvodu customizace.
 * Zákazníci si mohou registrovat vlastní validační funkce pod vlastním klíčem.
 * - Určeno pro uchování v reduxu. Proto nemůže být funkce, ale pouze klíč.
 * - V `InstantValidation.ts` pak existuje mapování z klíčů na konkrétní validační funkce.
 */
export type CustomizableInstantValidationFnKey = InstantValidationFnKey | string;

/**
 * Výčet známých validačních funkcí pro "explicit"-validaci. Jde o součást {@link CustomizableExplicitValidationFnKey}.
 */
export enum ExplicitValidationFnKey {
    regex = "regex",
    maxLength = "maxLength",
    minValue = "minValue",
    maxValue = "maxValue",
}

/**
 * Klíč validační funkce pro "explicit"-validaci. Kromě {@link ExplicitValidationFnKey} je možné jako klíč uvést "libovolný" řetězec z důvodu customizace.
 * Zákazníci si mohou registrovat vlastní validační funkce pod vlastním klíčem.
 * - Určeno pro uchování v reduxu. Proto nemůže být funkce, ale pouze klíč.
 * - V `ExplicitValidation.ts` pak existuje mapování z klíčů na konkrétní validační funkce.
 */
export type CustomizableExplicitValidationFnKey = ExplicitValidationFnKey | string;

/**
 * Identifikace volání "instant"-validační funkce. Definuje funkci, která se má provolat včetně parametrů, které se jí mají předat.
 * Identifikace je uložitelná v reduxu.
 * - Týká se validačních funkcí z `InstantValidation.ts`.
 * - Více viz {@link CustomizableInstantValidationFnKey}.
 */
export interface IInstantValidationFnDescription {
    readonly fnKey: CustomizableInstantValidationFnKey;
    readonly args: TypedObject<ReduxCompatibleValue>;
}

/**
 * Identifikace volání "explicit"-validační funkce. Definuje funkci, která se má provolat včetně parametrů, které se jí mají předat.
 * Identifikace je uložitelná v reduxu.
 * - Týká se validačních funkcí z `ExplicitValidation.ts`.
 * - Více viz {@link CustomizableExplicitValidationFnKey}.
 */
export interface IExplicitValidationFnDescription {
    /**
     * Klíč funkce, která se má provolat.
     */
    readonly fnKey: CustomizableExplicitValidationFnKey;
    /**
     * Parametry, které se funkci předají.
     */
    readonly args: TypedObject<ReduxCompatibleValue>;
    /**
     * Lokalizační klíč, který se použije pro chybovou hlášku ({@link IExplicitValidationError}), pokud validace neprojde.
     * - Používá se především při načítání lokalizací (pro {@link ValidationErrorWrapper}).
     * - Volitelně je možné použít pro customizaci chybové hlášky. K tomu ale zatím (září 2020) není důvod.
     */
    readonly locKey: string;
}

/**
 * Dílčí informace, proč neprošla validace. (Dílčí v tom, že důvodů, proč komponenta není validní, může být více. Tj. `IExplicitValidationError[]`.)
 */
export interface IExplicitValidationError {
    /**
     * Lokalizační klíč. Např. "GUI-123". Je možné nezadat, pak je ale třeba zadat {@link message}.
     */
    readonly locKey?: string;
    /**
     * Lokalizovaná zpráva určená k přímému zobrazení klientovi.
     * Volitelný údaj. Pokud zadán, pak se již nezadává {@link locKey}.
     */
    readonly message?: string;
    /**
     * Doplňuje {@link locKey}.
     * Parametry, které budou dosazeny za zástupné znaky v lokalizovaném textu.
     */
    readonly locArgs?: string[];

    /**
     * Udává typ chyby
     */
    readonly errorType?: ValidationErrorType;
}

/**
 * Lokalizovaná verze {@link IExplicitValidationError}.
 */
export interface ILocalizedExplicitValidationError {
    /**
     * Lokalizovaná zpráva určená k přímému zobrazení klientovi.
     */
    message: string;
}

/**
 * Signatura "instant"-validačních funkcí. Tj. funkcí validujících okamžitě při psaní - viz `InstantValidation.ts`.
 */
export type InstantValidationFunction = (
    value: ControlReduxRawValue,
    args: TypedObject<ReduxCompatibleValue>,
) => boolean;

/**
 * Signatura "explicit"-validačních funkcí. Tj. funkcí validujících pouze na vyžádání - viz `ExplicitValidation.ts`.
 */
export type ExplicitValidationFunction = (
    /**
     * Hodnota určená k validaci.
     */
    value: ControlReduxRawValue,
    /**
     * Parametry předávané - navíc k `value` - do validační funkce.
     * Např. `{maxLength: 10}` pro `validateMaxLength`.
     */
    args: TypedObject<ReduxCompatibleValue>,
    /**
     * Lokalizační klíč, který se použije pro návratovou chybovou hlášku při provolání validační funkce, pokud validace neprojde.
     */
    locKey: string,
) => IExplicitValidationError | null;
